import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { IApplicationState, AppActionCreators, AdpDonorActionCreators } from '@nkr/store/index';
import { IBaseResponse, IAdpVoucherHolderGetResponse, IAdpVoucherHolderCheckMissingPdfResponse, IAdpVoucherHolder } from '@nkr/models/index';
import { ReviewedStatus } from './reviewed-status';
import { AbnormalStatus } from './abnormal-status';
import { IFilterData } from './filter-data';
import { downloadFile } from '@nkr/services/download-file';
import Filter from './filter';
import MarkReviewed from './mark-reviewed-modal';
import MissingPdfs from './missing-pdf-modal';
import Grid from './grid';

interface IOwnProps { }

interface IAdpVoucherHolderStateProps { }

interface IAdpVoucherHolderDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getAdpVoucherHolders: () => Promise<IAdpVoucherHolderGetResponse>,
    updateVoucherHolderScreeningStatus: (voucherHolderId: number, isReviewed: boolean) => Promise<IBaseResponse>,
    sendAbnormalEmail: (voucherHolderId: number) => Promise<IBaseResponse>,
    getVoucherHolderScreeningResults: (voucherHolderId: number) => Promise<any>,
    checkMissingPdfs: (voucherHolderIds: number[]) => Promise<IAdpVoucherHolderCheckMissingPdfResponse>,
    printAll: (voucherHolderIds: number[]) => Promise<IBaseResponse>
}

type TAdpVoucherHolderProps = IAdpVoucherHolderStateProps & IAdpVoucherHolderDispatchProps & IOwnProps

interface IState {
    voucherHolders: IAdpVoucherHolder[],
    foundPdfsToPrint: IAdpVoucherHolder[],
    filter: IFilterData,
    showCompletedRows: boolean,
    search: string,
    status: number,
    markReviewed: {
        show: boolean,
        data: IAdpVoucherHolder
    },
    missingPdfs: {
        show: boolean,
        list: IAdpVoucherHolder[]
    }
}

class AdpVoucherHolder extends React.Component<TAdpVoucherHolderProps, IState> {
    readonly state: IState = {
        voucherHolders: [],
        foundPdfsToPrint: [],
        showCompletedRows: false,
        filter: {
            registrationStartDate: '',
            registrationEndDate: moment().format('MM/DD/YYYY'),
            reviewedStatus: ReviewedStatus.NotReviewed,
            abnormalStatus: AbnormalStatus.Abnormal,
            resultsDate: ''
        },
        search: '',
        status: 0,
        markReviewed: {
            show: false,
            data: null
        },
        missingPdfs: {
            show: false,
            list: []
        }
    }

    componentDidMount() {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting data, one moment please...').then(() => {
            this.props.getAdpVoucherHolders().then(response => {
                this.setState({
                    voucherHolders: response.data.results,
                });

                this.props.hideLoading();
            }).catch(() => {
                this.props.hideLoading();
            });
        }).catch(() => {
            this.props.hideLoading();
        });
    }

    getFilteredData = (): IAdpVoucherHolder[] => {
        let filterRegistrationStartDate = moment(this.state.filter.registrationStartDate, 'MM/DD/YYYY', true);
        let filterRegistrationEndDate = moment(this.state.filter.registrationEndDate, 'MM/DD/YYYY', true);
        let filterResultsDate = moment(this.state.filter.resultsDate, 'MM/DD/YYYY', true);

        let list = this.state.voucherHolders.filter(x => {
            const registrationDate   = moment(x.registrationDate, 'MM-DD-YYYY');
            const resultsDate        = moment(x.lastScreeningResultsDateTime, 'MM-DD-YYYY');
            const fitsAbnormalSearch =  (x.isAbnormalScreening || this.state.filter.abnormalStatus != AbnormalStatus.Abnormal);
            const fitsReviewedSearch = this.state.filter.reviewedStatus == ReviewedStatus.All
                    || (x.isAbnormalScreeningReviewed == false && this.state.filter.reviewedStatus == ReviewedStatus.NotReviewed)
                    || (x.isAbnormalScreeningReviewed == true && this.state.filter.reviewedStatus == ReviewedStatus.Reviewed)
            ;
            const fitsRegistrationStart     = !filterRegistrationStartDate.isValid() 
                                            || filterRegistrationStartDate.isSameOrBefore(registrationDate,'day');
            const fitsRegistrationEnd       = !filterRegistrationEndDate.isValid()
                                            || filterRegistrationEndDate.isSameOrAfter(registrationDate,'day');
            const fitsRegistrationDate      = fitsRegistrationStart && fitsRegistrationEnd;
            const fitsResultsDate           = !filterResultsDate.isValid()
                                            || filterResultsDate.isSame(resultsDate,'day');
                                            
            return fitsAbnormalSearch && fitsReviewedSearch && fitsRegistrationDate && fitsResultsDate;
        });

        return list;
    }

    continuePrintAll = () => {
        let list = this.state.foundPdfsToPrint.map(x => x.id);

        this.props.showLoading('Generating pdf...').then(() => {
            this.props.printAll(list).then(response => {
                if (response.data.success) {
                    let blob = response.data.message;

                    const link = document.createElement('a');
                    link.href = 'data:application/pdf;base64,' + blob;
                    link.download = 'file.pdf';

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                this.props.hideLoading();
            }).catch(() => {
                this.props.hideLoading();
            });
        });
    }

    handleConfirmMarkReviewed = (id: number) => {
        let data = this.state.voucherHolders.find(x => x.id == id);

        this.setState({
            markReviewed: {
                show: true,
                data
            }
        });
    }

    handleMarkReviewedYes = () => {
        let voucherHolderId = this.state.markReviewed.data.id;

        this.setState({
            markReviewed: {
                show: false,
                data: null
            }
        }, () => {
            this.props.showLoading('Updating data, one moment please...').then(() => {
                this.props.updateVoucherHolderScreeningStatus(voucherHolderId, true).then(response => {
                    this.getData();
                });
            }).catch(() => {
                this.props.hideLoading();
            });
        })
    }

    handleMarkReviewedNo = () => {
        this.setState({
            markReviewed: {
                show: false,
                data: null
            }
        });
    }

    handleOnClickSendEmail = (id: number) => {
        let data = this.state.voucherHolders.find(x => x.id == id);

        this.props.showLoading("Updating data, one moment please...")
            .then(() => {
                this.props
                    .sendAbnormalEmail(id)
                    .then(response => { this.getData(); })
                    .catch(() => {
                        this.props.hideLoading();
                    })
                ;
            })
        ;
    }

    handlePrintAllShowing = () => {
        let currentViewVoucherHolders = this.getFilteredData();

        if (currentViewVoucherHolders.length == 0) {
            alert('No voucher holders are currently in view')
        } else {
            let list = currentViewVoucherHolders.map(x => x.id);

            this.props.showLoading('Checking for any missing pdfs...').then(() => {
                this.props.checkMissingPdfs(list).then(response => {
                    if (response.data.success) {
                        let { notFounds: missingPdfs, founds: foundPdfsToPrint } = response.data.results;

                        if (missingPdfs.length > 0) {
                            this.setState({
                                missingPdfs: {
                                    show: true,
                                    list: missingPdfs
                                },
                                foundPdfsToPrint,
                            })
                        }
                        else {
                            this.setState({
                                missingPdfs: {
                                    show: false,
                                    list: []
                                },
                                foundPdfsToPrint
                            }, () => {
                                this.continuePrintAll();
                            });
                        }
                    }

                    this.props.hideLoading();
                }).catch(() => {
                    this.props.hideLoading();
                });
            });
        }
    }

    handleMissingPdfsOk = () => {
        this.setState({
            missingPdfs: {
                show: false,
                list: []
            }
        }, () => {
            this.continuePrintAll();
        });
    }

    handleMissingPdfsCancel = () => {
        this.setState({
            missingPdfs: {
                show: false,
                list: []
            }
        });
    }

    handleFilterChange = (property: string, value: any) => {
        let filter = _.cloneDeep(this.state.filter);

        if (property == 'registrationStartDate') {
            filter.registrationStartDate = value;
        }

        if (property == 'registrationEndDate') {
            filter.registrationEndDate = value;
        }

        if (property == 'resultsDate') {
            filter.resultsDate = value;
        }

        if (property == 'reviewedStatus') {
            filter.reviewedStatus = parseInt(value);
        }

        if (property == 'abnormalStatus') {
            filter.abnormalStatus = parseInt(value);
        }

        this.setState({
            filter
        });
    }

    handleResetFilters = () => {
        let filter = this.state.filter;

        filter.registrationStartDate = '';
        filter.registrationEndDate = moment().format('MM/DD/YYYY');
        filter.reviewedStatus = ReviewedStatus.NotReviewed;
        filter.resultsDate = '';

        this.setState({
            filter: filter
        });
    }

    handleGetScreeningResults = (voucherHolderId: number) => {
        this.props.getVoucherHolderScreeningResults(voucherHolderId).then((response: any) => {
            downloadFile(response.data, `voucher-${voucherHolderId}-results.pdf`, 'application/pdf');
        });
    }

    render = () => {
        const fullName = this.state.markReviewed.data != null ? this.state.markReviewed.data.firstName + ' ' + this.state.markReviewed.data.lastName : '';
        const filteredData = this.getFilteredData();
        const totalVoucherHolders = this.state.voucherHolders.length;
        const totalShowing = filteredData.length;

        return (
            <div className='container'>
                <div style={{ display: 'flex', alignItems: 'center', height: 30, marginBottom: 20 }}>
                    <div style={{ fontSize: 28, fontWeight: 500 }}>
                        Vouchers With Abnormal Results
                    </div>
                </div>

                <Filter
                    data={this.state.filter}
                    onChange={this.handleFilterChange}
                    onReset={this.handleResetFilters} />

                <div className='row' style={{ marginBottom: 10 }}>
                    <div className='col-xs-6 col-sm-4'>
                        <p style={{ paddingTop: 10, marginBottom: 0 }}><strong>Results:</strong> {totalVoucherHolders}</p>
                    </div>
                    <div className='col-xs-6 col-sm-4'>
                        <p style={{ paddingTop: 10, marginBottom: 0 }}><strong>Displayed:</strong> {totalShowing}</p>
                    </div>
                    <div className='col-xs-12 col-sm-4 text-right'>
                        <Button bsStyle='success' onClick={this.handlePrintAllShowing}>Print All Showing</Button>
                    </div>
                </div>

                <Grid
                    data={filteredData}
                    onGetScreeningResults={this.handleGetScreeningResults}
                    onConfirmMarkReviewed={this.handleConfirmMarkReviewed}
                    onClickSendEmail={this.handleOnClickSendEmail}
                />

                <MarkReviewed
                    show={this.state.markReviewed.show}
                    fullName={fullName}
                    onYes={this.handleMarkReviewedYes}
                    onNo={this.handleMarkReviewedNo} />

                <MissingPdfs
                    show={this.state.missingPdfs.show}
                    list={this.state.missingPdfs.list}
                    onOk={this.handleMissingPdfsOk}
                    onCancel={this.handleMissingPdfsCancel} />
            </div>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IAdpVoucherHolderStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IAdpVoucherHolderDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getAdpVoucherHolders: () => {
            return dispatch(AdpDonorActionCreators.getVoucherHolders());
        },
        getVoucherHolderScreeningResults: (voucherHolderId) => {
            return dispatch(AdpDonorActionCreators.getVoucherHolderScreeningResults(voucherHolderId));
        },
        updateVoucherHolderScreeningStatus: (voucherHolderId, isReviewed) => {
            return dispatch(AdpDonorActionCreators.updateVoucherHolderScreeningStatus(voucherHolderId, isReviewed))
        },
        sendAbnormalEmail: (voucherHolderId) => {
            return dispatch(AdpDonorActionCreators.sendAbnormalEmail(voucherHolderId))
        },
        checkMissingPdfs: (voucherHolderIds) => {
            return dispatch(AdpDonorActionCreators.checkForMissingPdf(voucherHolderIds))
        },
        printAll: (voucherHolderIds) => {
            return dispatch(AdpDonorActionCreators.printAll(voucherHolderIds))
        }
    };
}

export default connect<IAdpVoucherHolderStateProps, IAdpVoucherHolderDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(AdpVoucherHolder);
